import Backbone from "backbone";
import * as _ from "underscore";

import readlangClient from "readlangClient";
import * as utils from "shared/utils";

const indexOfPhrase = (splitPhrases, splitPhrase) => {
  let phraseIndex = -1;

  _.any(splitPhrases, (phrase, index) => {
    if (
      _.every(
        phrase.split,
        (word, wordIndex) => word === splitPhrase[wordIndex]
      )
    ) {
      phraseIndex = index;
      return true;
    }
  });

  return phraseIndex;
};

export default class AllPhrasesIndex {
  phraseIndex = {};
  filter: any;

  constructor() {
    _.extend(this, Backbone.Events);
  }

  /**
   * Fetch all the user's words and phrases for a given language from the server.
   *
   * @param {Object} options - Options for fetching phrases.
   * @param {string} options.language - The language to fetch phrases for.
   * @param {"readyToLearn"} [options.filter] - Optional filter to apply to the phrases.
   */
  async fetch(options) {
    const that = this;

    if ((this.filter || options.filter) && options.filter !== this.filter) {
      this.phraseIndex = {};
    }
    this.filter = options.filter;

    // fetch all user's phrases from server (except deleted ones)
    // return all phrases, indexed by the first word
    //
    // TODO: this will make 2 requests if called twice in quick succession
    if (this.phraseIndex[options.language]) {
      return that.phraseIndex[options.language];
    }

    const { data } = await readlangClient.request(
      "GET",
      "/api/listAllWords/" + options.language,
      { filter: this.filter }
    );

    // Check for error, which happens if we've reached the free limit
    if (data.error && data.error.type) {
      throw new Error(data.error.type);
    }

    const allWords = data;
    const phraseIndex = {};
    _.each(allWords, (phrase) => {
      const phraseWords = phrase.toLowerCase().split(utils.notWordRegExp());
      const firstWord = phraseWords[0];
      phraseIndex[firstWord] =
        typeof phraseIndex[firstWord] === "object"
          ? phraseIndex[firstWord]
          : [];
      phraseIndex[firstWord].push({
        whole: phrase,
        split: phraseWords,
      });
    });
    that.phraseIndex[options.language] = phraseIndex;
    return phraseIndex;
  }

  addToPhraseIndex(language, phrase) {
    const that = this;

    const phraseIndex = that.phraseIndex[language];
    if (!phraseIndex) {
      // this language index hasn't been fetched
      return;
    }

    const phraseLower = phrase.toLowerCase();
    const splitPhrase = phraseLower.split(utils.notWordRegExp());
    const phrases = phraseIndex[splitPhrase[0]] || [];

    const index = indexOfPhrase(phrases, splitPhrase);

    if (index === -1) {
      phrases.push({
        whole: phrase,
        split: splitPhrase,
      });
      phraseIndex[splitPhrase[0]] = phrases;
      (that as any).trigger("add", {
        language: language,
        split: splitPhrase,
        whole: phrase,
      });
    }
  }

  /**
   * @returns {{
   *   [firstWordLower: string]: { whole: string; split: string[];}
   * }} the new phrase index for this language
   **/
  removeFromPhraseIndex(language, phrase) {
    const that = this;

    const phraseIndex = that.phraseIndex[language];
    if (!phraseIndex) {
      // this language index hasn't been fetched
      return;
    }

    const phraseLower = phrase.toLowerCase();
    const splitPhrase = phraseLower.split(utils.notWordRegExp());

    const phrases = phraseIndex[splitPhrase[0]] || [];
    const index = indexOfPhrase(phrases, splitPhrase);
    if (index !== -1) {
      phrases.splice(index, 1);
      phraseIndex[splitPhrase[0]] = phrases;
      if (indexOfPhrase(phrases, splitPhrase) === -1) {
        // only trigger remove if it's the last one in case of duplicate phrases
        (that as any).trigger("remove", {
          language: language,
          split: splitPhrase,
          whole: phrase,
        });
      }
    }
    return phraseIndex;
  }

  /**
   * @returns {{
   *   [firstWordLower: string]: { whole: string; split: string[];}
   * }} the new phrase index for this language
   **/
  wordRecalled(language, phrase) {
    if (this.filter === "readyToLearn") {
      return this.removeFromPhraseIndex(language, phrase);
    }

    return this.phraseIndex[language];
  }
}
